import { gql, useMutation, useQuery } from '@apollo/client';
import { useAssistanceContext } from './AssistanceContext.tsx';
import * as React from 'react';
import { useState } from 'react';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { useTranslation } from 'react-i18next';
import AssistanceSection from './AssistanceSection.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faSparkles, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { MAPPING_MUTATIONS, MAPPINGS_QUERY } from './queries.ts';
import { useToaster } from '../../../core/components/Toast.tsx';

const LoadingIcon = withIcon(faSpinnerThird);
const MappingIcon = withIcon(faSparkles);

const MAPPING_CONFIGS = {
    client: {
        key: 'clientMapping',
        queryField: 'clientMappings',
        targetField: 'clientId',
        variableName: 'clientMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateClientMappingMutation,
        isHeaderField: true,
    },
    contact: {
        key: 'contactMapping',
        queryField: 'contactMappings',
        targetField: 'targetContactId',
        variableName: 'contactMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateContactMappingMutation,
        isHeaderField: true,
    },
    article: {
        key: 'articleMapping',
        queryField: 'articleMappings',
        targetField: 'targetArticleNumber',
        variableName: 'articleMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateArticleMappingMutation,
        isHeaderField: false,
    },
    vatIdCheck: {
        key: 'vatIdCheckMapping',
        queryField: 'vatIdCheckMappings',
        targetField: 'vatId',
        variableName: 'vatIdCheckMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateVatIdCheckMappingMutation,
        isHeaderField: true,
    },
    invoiceAddress: {
        key: 'invoiceAddressMapping',
        queryField: 'invoiceAddressMappings: addressMappings',
        targetField: 'targetAddressId',
        variableName: 'invoiceAddressMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateAddressMappingMutation,
        isHeaderField: true,
    },
    deliveryAddress: {
        key: 'deliveryAddressMapping',
        queryField: 'deliveryAddressMappings: addressMappings',
        targetField: 'targetAddressId',
        variableName: 'deliveryAddressMappingLookupDefinitionId',
        mutation: MAPPING_MUTATIONS.updateAddressMappingMutation,
        isHeaderField: true,
    },
} as const;

const getMappingConfig = (fieldTypeName: string, fieldName: string) => {
    if (fieldTypeName === 'CustomerField') {
        return MAPPING_CONFIGS.client;
    } else if (fieldTypeName === 'ContactField') {
        return MAPPING_CONFIGS.contact;
    } else if (fieldName === 'invoice_address') {
        return MAPPING_CONFIGS.invoiceAddress;
    } else if (fieldName === 'delivery_address') {
        return MAPPING_CONFIGS.deliveryAddress;
    } else if (fieldName === 'article_number') {
        return MAPPING_CONFIGS.article;
    } else if (fieldName === 'vat_id') {
        return MAPPING_CONFIGS.vatIdCheck;
    }
};

export const useMappingsQuery = (record) => {
    const config = record?.channel?.masterDataConfig || {};
    const shouldSkip = !record?.id;
    const recordInfo = record?.id ? `${record.documentType}/${record.id}` : '';

    // gql requires UUID! even if request will not be included, so it is safe to just mock it here
    const mockUuid = '00000000-0000-0000-0000-000000000000';
    const variables = {
        recordInfo,
        clientMappingLookupDefinitionId: config.clientMappingLookupDefinitionId || mockUuid,
        includeClient: Boolean(config.clientMappingLookupDefinitionId),

        contactMappingLookupDefinitionId: config.contactMappingLookupDefinitionId || mockUuid,
        includeContact: Boolean(config.contactMappingLookupDefinitionId),

        articleMappingLookupDefinitionId: config.articleMappingLookupDefinitionId || mockUuid,
        includeArticle: Boolean(config.articleMappingLookupDefinitionId),

        vatIdCheckMappingLookupDefinitionId: config.vatIdCheckMappingLookupDefinitionId || mockUuid,
        includeVatIdCheck: Boolean(config.vatIdCheckMappingLookupDefinitionId),

        invoiceAddressMappingLookupDefinitionId: config.invoiceAddressMappingLookupDefinitionId || mockUuid,
        includeInvoiceAddress: Boolean(config.invoiceAddressMappingLookupDefinitionId),

        deliveryAddressMappingLookupDefinitionId: config.deliveryAddressMappingLookupDefinitionId || mockUuid,
        includeDeliveryAddress: Boolean(config.deliveryAddressMappingLookupDefinitionId),
    };

    return useQuery(MAPPINGS_QUERY, {
        variables,
        skip: shouldSkip,
    });
};

const NOOP_MUTATION = gql`
    mutation {
        __typename
    }
`;

export const useUpdateMapping = (fieldTypeName: string, fieldName: string, id: string) => {
    const mappingConfig = getMappingConfig(fieldTypeName, fieldName);
    const { record } = useAssistanceContext();

    const mutation = mappingConfig?.mutation;
    const [updateMapping] = useMutation(mutation || NOOP_MUTATION);

    if (!mutation) {
        return Promise.resolve();
    }

    return (data) => {
        if (!mappingConfig || !mutation) return;

        const lookupDefinitionId = record?.channel?.masterDataConfig?.[mappingConfig.variableName];

        return updateMapping({
            variables: {
                id,
                lookupDefinitionId,
                ...data,
            },
        });
    };
};

export const useMapping = (fieldTypeName: string, fieldName: string, value: any) => {
    const { mappings: mappingsByQueryField } = useAssistanceContext();

    const mappingConfig = getMappingConfig(fieldTypeName, fieldName);
    if (!mappingConfig) return null;

    const fieldMappings = mappingsByQueryField?.[mappingConfig.queryField.split(':')[0]] || [];

    // for header fields there is only up to one mapping created so we can just use it
    const targetFieldValueMappings = fieldMappings.filter((mapping) => mapping[mappingConfig.targetField] === value);
    const targetFieldTypeMappings = fieldMappings.length === 1 && mappingConfig.isHeaderField ? fieldMappings : [];
    const mappings = targetFieldValueMappings.length > 0 ? targetFieldValueMappings : targetFieldTypeMappings;

    if (mappings?.length > 1) {
        console.warn(`Multiple mappings found for ${mappingConfig.queryField} with value ${value}.`);

        // highConfidence > isIgnored > regular as highConfidence probably will trump the others
        mappings.sort((a, b) => {
            if (a.highConfidence !== b.highConfidence) {
                return a.highConfidence ? -1 : 1;
            } else if (a.isIgnored !== b.isIgnored) {
                return a.isIgnored ? 1 : -1;
            } else {
                return 0;
            }
        });
    }

    return mappings?.[0];
};

const MappingRadioItem = ({
    className,
    children,
    value,
    title,
    description,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenu.RadioItem> & {
    description?: string;
}) => (
    <DropdownMenu.RadioItem {...props} value={value} className={classnames('group', className)}>
        <div className="flex flex-col gap-1 max-w-64">
            <h3 className="text-content-strong group-aria-checked:text-content-brand-strong">{title}</h3>
            <p className="text-content-medium text-sm">{description}</p>
        </div>
    </DropdownMenu.RadioItem>
);

export const MappingDropdownMenu = ({ mapping, onChange, hideValue = false, align = 'end', ...props }: any) => {
    const { t } = useTranslation('assistance');
    const { publishToast } = useToaster();

    const [isLoading, setIsLoading] = useState(false);

    const value = mapping?.isIgnored ? 'ignored' : mapping?.highConfidence ? 'preferred' : 'regular';
    const handleValueChange = async (value) => {
        setIsLoading(true);
        try {
            if (value === 'ignored') {
                await onChange({ isIgnored: true, highConfidence: false });
                publishToast({ status: 'success', description: t('mapping.ignoredUpdateSuccess') });
            } else if (value === 'preferred') {
                await onChange({ isIgnored: false, highConfidence: true });
                publishToast({ status: 'success', description: t('mapping.preferredUpdateSuccess') });
            } else {
                await onChange({ isIgnored: false, highConfidence: false });
                publishToast({ status: 'success', description: t('mapping.regularUpdateSuccess') });
            }
        } catch (error) {
            publishToast({ status: 'error', description: t('mapping.updateError') });
        }
        setIsLoading(false);
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <AssistanceSection.FieldGroupButton {...props} isLoading={isLoading}>
                        <span>
                            {isLoading ? (
                                <LoadingIcon className="text-content-brand-strong text-sm animate-spin" />
                            ) : (
                                <MappingIcon />
                            )}
                        </span>
                        {!hideValue && <span>{t(`mapping.${value}`)}</span>}
                    </AssistanceSection.FieldGroupButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content align={align} onCloseAutoFocus={(e) => e.preventDefault()}>
                    <DropdownMenu.RadioGroup value={value} onValueChange={handleValueChange}>
                        <MappingRadioItem
                            value="preferred"
                            title={t('mapping.preferredTitle')}
                            description={t('mapping.preferredDescription')}
                        />

                        <MappingRadioItem
                            value="regular"
                            title={t('mapping.regularTitle')}
                            description={t('mapping.regularDescription')}
                        />

                        <MappingRadioItem
                            value="ignored"
                            title={t('mapping.ignoredTitle')}
                            description={t('mapping.ignoredDescription')}
                        />
                    </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
            </DropdownMenu>
        </>
    );
};
